import { useApolloClient, useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  AnnotatedField,
  CurrencyAmountUI,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { dateSchema, decimalSchema } from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense, useEffect } from "react";
import { z } from "zod";
import { BusinessEntityCard } from "~/components/BusinessEntity/BusinessEntityCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { ProductCard } from "~/components/ProductCard";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

const formSchema = z
  .object({
    organisationId: z.string().min(1),
    businessEntityId: z.string().min(1),
    productId: z.string().min(1),
    openedAt: dateSchema,
    feeRate: decimalSchema,
    status: z.enum(["PENDING", "ACTIVE", "CLOSED"]),
    custody: z.enum(["YES", "NO"]).optional(),
    requiresCustodySelection: z.boolean(),
    settlementInstructionAccountId: z.string().optional(),
    requiresSettlementInstructionSelection: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.requiresCustodySelection && value.custody === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "Required",
        path: ["custody"],
      });
    }
    if (
      value.requiresSettlementInstructionSelection &&
      !value.settlementInstructionAccountId
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Required",
        path: ["settlementInstructionAccountId"],
      });
    }
    if (
      value.settlementInstructionAccountId &&
      !value.settlementInstructionAccountId.startsWith("asset:") &&
      !value.settlementInstructionAccountId.startsWith("beneficiary:")
    ) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid settlement destination",
        path: ["settlementInstructionAccountId"],
      });
    }
  });

type FormValues = z.input<typeof formSchema>;

type SettlementInstructionAccount =
  | { kind: "asset"; id: string }
  | { kind: "beneficiary"; id: string };

const parseSettlementInstructionAccountId = (
  value: string | undefined,
): SettlementInstructionAccount | undefined => {
  if (!value) {
    return undefined;
  }
  const [kind, id] = value.split(":", 2);
  if (!id) {
    return undefined;
  }
  if (kind === "asset") {
    return { kind, id };
  }
  if (kind === "beneficiary") {
    return { kind, id };
  }
  return undefined;
};

export const Route = createFileRoute("/__auth/assets_/new")({
  head: () => opsHead("New Asset"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const client = useApolloClient();

  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(
      `query AdminNewAssetForm {
        organisations {
          id
          name

          ... OrganisationCardFields

          businessEntities {
            id
            canOpenCustodyAccounts

            beneficiaryAccounts {
              id
              displayName
              kind
            }

            ... BusinessEntityCardFields
          }

          assets {
            id
            name
            businessEntity {
                id
                name
            }
          }
        }

        custodyAccounts {
            id
            product {
                id
            }
        }

        products {
          id
          available

          ... on FundProduct {
            latestUnitPrice {
              price
              targetUnit
            }
          }

          ... ProductCardFields
        }
      }`,
    ),
  );

  const [createAsset] = useMutation(
    graphql(`
      mutation AdminAssetNew($input: CreateAssetInput!) {
        createAsset(input: $input) {
          asset {
            id
            businessEntity {
              id
            }
          }
        }
      }
    `),
    {
      onError(error) {
        const errorMessage =
          error.graphQLErrors?.[0]?.message || "Failed to create asset";

        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating asset",
          description: errorMessage,
        });
      },
      onCompleted: (data) => {
        if (data.createAsset.asset.businessEntity?.id !== undefined) {
          client.cache.evict({
            id: `BusinessEntity:${data.createAsset.asset.businessEntity.id}`,
            fieldName: "assets",
          });
        }
        void navigate({
          to: "/assets/$assetId",
          params: { assetId: data.createAsset.asset.id },
        });
      },
    },
  );

  const form = useAppForm({
    defaultValues: {
      feeRate: new Decimal(0),
      openedAt: new Date(),
      status: "ACTIVE",
      requiresCustodySelection: false,
      requiresSettlementInstructionSelection: false,
    } as FormValues,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error creating asset",
        });
        return;
      }
      const settlementInstructionAccountId =
        parsed.data.settlementInstructionAccountId;
      const settlementInstructionAccount = parseSettlementInstructionAccountId(
        settlementInstructionAccountId,
      );
      createAsset({
        variables: {
          input: {
            organisationId: parsed.data.organisationId,
            businessEntityId: parsed.data.businessEntityId,
            productId: parsed.data.productId,
            openedAt: parsed.data.openedAt,
            feeRate: parsed.data.feeRate,
            status: parsed.data.status,
            custody: parsed.data.custody === "YES",
            settlementInstructionAssetId:
              settlementInstructionAccount?.kind === "asset"
                ? settlementInstructionAccount.id
                : undefined,
            settlementInstructionAccount: (() => {
              if (settlementInstructionAccount?.kind === "beneficiary") {
                return {
                  beneficiaryAccountId: settlementInstructionAccount.id,
                };
              }
              if (settlementInstructionAccount?.kind === "asset") {
                return { assetId: settlementInstructionAccount.id };
              }
              return undefined;
            })(),
          },
        },
      });
    },
  });

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedOrganisation = data.organisations?.find(
    (o) => o.id === selectedOrganisationId,
  );
  const settlementInstructionSelection = useStore(
    form.store,
    (state) => state.values.settlementInstructionAccountId ?? "",
  );
  useEffect(() => {
    if (
      selectedOrganisation &&
      selectedOrganisation.businessEntities &&
      selectedOrganisation.businessEntities.length === 1
    ) {
      form.setFieldValue(
        "businessEntityId",
        selectedOrganisation.businessEntities[0].id,
      );
    }
  }, [form, selectedOrganisation]);

  const filteredProductOptions = data.products
    ?.slice()
    ?.filter(
      (p) =>
        p.available &&
        (p.__typename === "BankAccountProduct" ||
          p.__typename === "FundProduct"),
    )
    ?.sort((a, b) => a.internalName.localeCompare(b.internalName));
  const filteredProductOptionsById = new Map(
    filteredProductOptions?.map((p) => [p.id, p]),
  );
  const productsWithCustodyAccount = new Set(
    data.custodyAccounts
      ?.map((custodyAccount) => custodyAccount.product?.id)
      .filter((productId): productId is string => !!productId),
  );

  return (
    <AdminPageContainer testId="admin-assets-new">
      <AdminPageHeader
        title="New Asset"
        subtitle="Create a new first-party bank account or fund asset"
      />
      <div className="flex max-w-2xl flex-col gap-6">
        <Alert
          kind={"info"}
          title="Term Deposits"
          description={
            <Txt variant="body">
              Creating a new term deposit? Use the{" "}
              <Link to="/term-deposits/new" className="underline">
                new term deposit
              </Link>{" "}
              page instead.
            </Txt>
          }
        />
        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
            className="space-y-4"
          >
            <form.AppField name="organisationId">
              {(field) => (
                <field.SelectField
                  label="Organisation"
                  placeholder="Select an organisation..."
                  onValueChange={(e) => {
                    field.handleChange(e);
                    field.form.setFieldValue("businessEntityId", "");
                    field.form.setFieldValue(
                      "settlementInstructionAccountId",
                      undefined,
                    );
                  }}
                >
                  {data.organisations
                    ?.slice()
                    ?.sort((a, b) => a.name.localeCompare(b.name))
                    .map((o) => (
                      <field.SelectItem key={o.id} value={o.id}>
                        <OrganisationCard organisation={o} />
                      </field.SelectItem>
                    ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="businessEntityId">
              {(field) => (
                <field.SelectField
                  label="Business Entity"
                  placeholder={
                    selectedOrganisation === undefined
                      ? "Select organisation first"
                      : "Select a business entity..."
                  }
                  disabled={selectedOrganisation === undefined}
                >
                  {selectedOrganisation?.businessEntities
                    ?.slice()
                    ?.sort((a, b) => a.name.localeCompare(b.name))
                    .map((be) => (
                      <field.SelectItem key={be.id} value={be.id}>
                        <BusinessEntityCard businessEntity={be} />
                      </field.SelectItem>
                    ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="productId">
              {(field) => (
                <field.SelectField
                  label="Product"
                  placeholder="Select a product..."
                  onValueChange={(value) => {
                    field.handleChange(value);
                    const requiresCustodySelection =
                      productsWithCustodyAccount.has(value);
                    const requiresSettlementInstructionSelection =
                      filteredProductOptionsById.get(value)?.__typename ===
                      "FundProduct";
                    field.form.setFieldValue(
                      "requiresCustodySelection",
                      requiresCustodySelection,
                    );
                    field.form.setFieldValue(
                      "requiresSettlementInstructionSelection",
                      requiresSettlementInstructionSelection,
                    );
                    if (!requiresCustodySelection) {
                      field.form.setFieldValue("custody", undefined);
                    }
                    if (!requiresSettlementInstructionSelection) {
                      field.form.setFieldValue(
                        "settlementInstructionAccountId",
                        undefined,
                      );
                    }
                  }}
                >
                  {filteredProductOptions?.map((p) => (
                    <field.SelectItem key={p.id} value={p.id}>
                      <ProductCard product={p} />
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.Subscribe selector={(state) => state.values.productId}>
              {(productId) => {
                const selectedProduct =
                  filteredProductOptionsById.get(productId);
                if (
                  !selectedProduct ||
                  selectedProduct.__typename !== "FundProduct" ||
                  !selectedProduct.latestUnitPrice
                ) {
                  return null;
                }
                return (
                  <Alert
                    kind="info"
                    description={
                      <Txt variant="caption">
                        The latest unit price for this product is{" "}
                        <CurrencyAmountUI
                          nonCopyable
                          amount={selectedProduct.latestUnitPrice.price}
                          currency={selectedProduct.latestUnitPrice.targetUnit}
                        />
                        . Please check that the price is consistent with the
                        account you've opened{" "}
                        {selectedProduct.provider?.name
                          ? `in ${selectedProduct.provider.name}`
                          : "with the provider"}
                        .
                      </Txt>
                    }
                  ></Alert>
                );
              }}
            </form.Subscribe>

            <form.Subscribe
              selector={(state) => ({
                requiresCustodySelection: state.values.requiresCustodySelection,
                productId: state.values.productId,
                businessEntityId: state.values.businessEntityId,
              })}
            >
              {({ requiresCustodySelection, productId, businessEntityId }) => {
                if (!requiresCustodySelection) {
                  return null;
                }
                const selectedBusinessEntity =
                  selectedOrganisation?.businessEntities?.find(
                    (be) => be.id === businessEntityId,
                  );
                const canOpenCustodyAccounts =
                  selectedBusinessEntity?.canOpenCustodyAccounts ?? false;
                return (
                  <form.AppField name="custody">
                    {(field) => (
                      <>
                        <field.SelectField
                          label="Is this asset custodially held?"
                          placeholder="Select yes or no..."
                          onValueChange={(value) => {
                            field.handleChange(value as "YES" | "NO");
                            if (value === "YES") {
                              field.form.setFieldValue(
                                "requiresSettlementInstructionSelection",
                                false,
                              );
                              field.form.setFieldValue(
                                "settlementInstructionAccountId",
                                undefined,
                              );
                            } else {
                              const requiresSettlementInstructionSelection =
                                filteredProductOptionsById.get(productId)
                                  ?.__typename === "FundProduct";
                              field.form.setFieldValue(
                                "requiresSettlementInstructionSelection",
                                requiresSettlementInstructionSelection,
                              );
                            }
                          }}
                        >
                          <field.SelectItem
                            value="YES"
                            disabled={!canOpenCustodyAccounts}
                          >
                            Yes
                          </field.SelectItem>
                          <field.SelectItem value="NO">No</field.SelectItem>
                        </field.SelectField>
                        {!canOpenCustodyAccounts && (
                          <Alert
                            kind="warning"
                            description={
                              <Txt variant="caption">
                                Business entity must be KYC verified to open a
                                custodially held account
                              </Txt>
                            }
                          />
                        )}
                      </>
                    )}
                  </form.AppField>
                );
              }}
            </form.Subscribe>

            <form.Subscribe
              selector={(state) => ({
                organisationId: state.values.organisationId,
                requiresSettlementInstructionSelection:
                  state.values.requiresSettlementInstructionSelection,
              })}
            >
              {({ requiresSettlementInstructionSelection, organisationId }) => {
                if (!requiresSettlementInstructionSelection) {
                  return null;
                }

                const settlementAssets = (
                  data.organisations?.find(
                    (organisation) => organisation.id === organisationId,
                  )?.assets ?? []
                )
                  .filter(
                    (a) =>
                      a.__typename === "FXAsset" ||
                      a.__typename === "BankAccountAsset",
                  )
                  .map((a) => ({
                    label: `${a.name} - ${a.businessEntity?.name}`,
                    value: a.id,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label));

                const settlementBeneficiaryAccounts = (
                  selectedOrganisation?.businessEntities ?? []
                )
                  .flatMap((businessEntity) => {
                    if (!businessEntity) {
                      return [];
                    }
                    const beneficiaryAccounts =
                      businessEntity.beneficiaryAccounts ?? [];
                    return beneficiaryAccounts
                      .filter(
                        (beneficiaryAccount) =>
                          beneficiaryAccount.kind === "VERIFIED_BENEFICIARY",
                      )
                      .map((account) => {
                        return {
                          label: `${account.displayName} - ${businessEntity.name}`,
                          value: account.id,
                        };
                      });
                  })
                  .sort((a, b) => a.label.localeCompare(b.label));
                const hasSettlementAssets = settlementAssets.length > 0;
                const hasBeneficiaryAccounts =
                  settlementBeneficiaryAccounts.length > 0;
                const shouldShowDivider =
                  hasSettlementAssets && hasBeneficiaryAccounts;

                return (
                  <form.AppField name="settlementInstructionAccountId">
                    {() => (
                      <AnnotatedField label="Settlement Account">
                        <Select
                          value={settlementInstructionSelection}
                          onValueChange={(value) => {
                            form.setFieldValue(
                              "settlementInstructionAccountId",
                              value,
                            );
                          }}
                          disabled={!organisationId}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                organisationId
                                  ? "Select a settlement account..."
                                  : "Select organisation first"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {hasSettlementAssets && (
                              <SelectGroup>
                                <SelectLabel>Settlement assets</SelectLabel>
                                {settlementAssets.map((asset) => (
                                  <SelectItem
                                    key={`asset-${asset.value}`}
                                    value={`asset:${asset.value}`}
                                  >
                                    {asset.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                            {shouldShowDivider && <SelectSeparator />}
                            {hasBeneficiaryAccounts && (
                              <SelectGroup>
                                <SelectLabel>Beneficiary accounts</SelectLabel>
                                {settlementBeneficiaryAccounts.map(
                                  (account) => (
                                    <SelectItem
                                      key={`beneficiary-${account.value}`}
                                      value={`beneficiary:${account.value}`}
                                    >
                                      {account.label}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectGroup>
                            )}
                          </SelectContent>
                        </Select>
                      </AnnotatedField>
                    )}
                  </form.AppField>
                );
              }}
            </form.Subscribe>

            <form.AppField name="status">
              {(field) => (
                <field.SelectField
                  label="Status"
                  placeholder="Select status..."
                >
                  {["PENDING", "ACTIVE", "CLOSED"].map((status) => (
                    <field.SelectItem key={status} value={status}>
                      {status}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="openedAt">
              {(field) => <field.DatePickerField label="Opened At" />}
            </form.AppField>

            <form.AppField name="feeRate">
              {(field) => (
                <field.PercentageField
                  label="Fee Rate"
                  scale="0-1"
                  decimalScale={3}
                />
              )}
            </form.AppField>

            <div className="flex justify-end">
              <form.SubmitButton
                childrenWhileSubmitting="Creating..."
                disabled={form.state.isSubmitting}
              >
                Create
              </form.SubmitButton>
            </div>
          </form>
        </form.AppForm>
      </div>
    </AdminPageContainer>
  );
}
